import { useEffect, useState } from 'react';
import { api, messageOf } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { ASSETS_BASE } from '../assets/manifest.js';
import { ItemIcon } from '../components/ItemIcon.js';

/** Quantos itens cabem numa bolsa — `MAX_INVENTORY` do motor. */
const BAG_LIMIT = 4;

interface LeaderboardEntry {
  readonly id: number;
  readonly username: string;
  readonly role: string;
  readonly avatarPortrait: string;
  readonly skirmishesPlayed: number;
  readonly skirmishesWon: number;
  readonly winRate: number;
  readonly eloRating: number;
  readonly favoriteClass: string;
}

interface ProfileUnit {
  readonly characterId: number;
  readonly name: string;
  readonly className: string;
  readonly portrait?: { sheet: string; index: number } | null;
  readonly mapSprite?: { sheet: string; index: number } | null;
  readonly battler?: string | null;
  readonly level: number;
  readonly exp: number;
  readonly stats: {
    readonly hp?: number;
    readonly str?: number;
    readonly mag?: number;
    readonly skl?: number;
    readonly spd?: number;
    readonly lck?: number;
    readonly def?: number;
    readonly res?: number;
  };
  readonly inventory: Array<{
    readonly weaponId?: number;
    readonly itemId?: number;
    readonly kind?: 'arma' | 'item';
    readonly usesLeft: number;
    readonly name?: string;
    readonly icon?: number;
    readonly iconSheet?: string;
    readonly type?: string;
  }>;
  readonly battles: number;
  readonly updatedAt: string;
}

interface InventoryItem {
  readonly contentType: 'weapons' | 'items';
  readonly contentId: number;
  readonly quantity: number;
  readonly name: string;
  readonly description: string;
  readonly icon: number;
  readonly iconSheet?: string | null;
  readonly type: string;
  /**
   * Para onde este item vai: a bolsa de um personagem (`batalha`), a ficha
   * dele (`ficha`, o reforço permanente) ou lugar nenhum por enquanto. Quem
   * decide é o servidor (`Oz\Content\ItemUse`), com a mesma regra da tela.
   */
  readonly use?: 'batalha' | 'ficha' | 'nenhum';
  readonly boost?: { readonly stat: string; readonly amount: number } | null;
  readonly acquiredAt: string;
}

const STAT_NAMES: Record<string, string> = {
  hp: 'PV', str: 'Força', mag: 'Magia', skl: 'Destreza',
  spd: 'Velocidade', lck: 'Sorte', def: 'Defesa', res: 'Resistência',
};

const AVAILABLE_PORTRAITS = [
  'Distortidus',
  'Mondona',
  'Actor1',
  'Actor2',
  'Actor3',
  'People1',
  'People2',
  'Evil',
  'Monster',
  'team-naux',
];

const AVAILABLE_CLASSES = [
  'Cavaleiro',
  'Espadachim',
  'Artista Marcial',
  'Mago',
  'Arqueiro',
  'Arruaceiro',
  'Sacerdote',
  'Espadachim Mágico',
];

function UnitPortrait({ unit }: { unit: ProfileUnit }): JSX.Element {
  if (unit.portrait && unit.portrait.sheet) {
    const col = unit.portrait.index % 4;
    const row = Math.floor(unit.portrait.index / 4);
    return (
      <div
        className="profile-unit-avatar"
        style={{
          backgroundImage: `url(${ASSETS_BASE}/img/faces/${unit.portrait.sheet}.webp)`,
          backgroundSize: '400% 200%',
          backgroundPosition: `${(col / 3) * 100}% ${(row / 1) * 100}%`,
        }}
        title={unit.name}
      />
    );
  }
  if (unit.battler) {
    // O battler de personagem é uma folha de combate lateral do MZ — 9 colunas
    // por 6 linhas de pose, em `sv_actors` (não existe pasta `battlers`). O
    // primeiro quadro, parado, é o que cabe no cartão.
    return (
      <div
        className="profile-unit-avatar battler-view"
        style={{
          backgroundImage: `url(${ASSETS_BASE}/img/sv_actors/${unit.battler}.webp)`,
          backgroundSize: '900% 600%',
          backgroundPosition: '0 0',
          backgroundRepeat: 'no-repeat',
        }}
        title={unit.name}
      />
    );
  }
  return (
    <div className="profile-unit-avatar placeholder" title={unit.name}>
      🛡️
    </div>
  );
}

export function CommunityPage({
  onOpenAuth,
}: {
  readonly onOpenAuth: () => void;
}): JSX.Element {
  const { user, stats, updateProfile } = useAuth();
  const [leaderboard, setLeaderboard] = useState<readonly LeaderboardEntry[]>([]);
  const [loadingLb, setLoadingLb] = useState(true);

  const [selectedPortrait, setSelectedPortrait] = useState(user?.avatarPortrait ?? 'Distortidus');
  const [selectedClass, setSelectedClass] = useState(stats?.favoriteClass ?? 'Cavaleiro');
  const [bioText, setBioText] = useState(user?.bio ?? '');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  const [rosterUnits, setRosterUnits] = useState<readonly ProfileUnit[]>([]);
  const [inventoryItems, setInventoryItems] = useState<readonly InventoryItem[]>([]);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [equipNotice, setEquipNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [equipping, setEquipping] = useState(false);

  useEffect(() => {
    if (user) {
      setSelectedPortrait(user.avatarPortrait);
      setBioText(user.bio ?? '');
    }
  }, [user]);

  useEffect(() => {
    if (stats) {
      setSelectedClass(stats.favoriteClass);
    }
  }, [stats]);

  const loadLeaderboard = async () => {
    try {
      setLoadingLb(true);
      const res = await fetch('/api/users/leaderboard');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { leaderboard: LeaderboardEntry[] };
      setLeaderboard(data.leaderboard);
    } catch {
      setLeaderboard([]);
    } finally {
      setLoadingLb(false);
    }
  };

  const loadRoster = async () => {
    if (!user) return;
    try {
      setLoadingRoster(true);
      const res = await fetch('/api/perfil/esquadrao', { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { units?: ProfileUnit[] };
      setRosterUnits(data.units ?? []);
    } catch {
      setRosterUnits([]);
    } finally {
      setLoadingRoster(false);
    }
  };

  const loadInventory = async () => {
    if (!user) return;
    try {
      setLoadingInventory(true);
      const res = await fetch('/api/economia/inventario', { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { items?: InventoryItem[] };
      setInventoryItems(data.items ?? []);
    } catch {
      setInventoryItems([]);
    } finally {
      setLoadingInventory(false);
    }
  };

  useEffect(() => {
    void loadLeaderboard();
  }, [stats]);

  useEffect(() => {
    if (user) {
      void loadRoster();
      void loadInventory();
    } else {
      setRosterUnits([]);
      setInventoryItems([]);
    }
  }, [user]);

  /*
   * Leva uma arma — ou um item de batalha — do inventário da conta para a
   * bolsa de um personagem.
   *
   * É o único caminho por onde o que se compra na loja, ou se ganha num
   * capítulo, chega à batalha: a partida lê a bolsa de cada personagem, e não
   * o inventário da conta.
   */
  const equip = async (item: InventoryItem, characterId: number) => {
    const unit = rosterUnits.find((u) => u.characterId === characterId);
    setEquipping(true);
    setEquipNotice(null);
    try {
      await api.post('/api/perfil/esquadrao/equipar', {
        characterId,
        contentType: item.contentType,
        contentId: item.contentId,
      });
      setEquipNotice({ ok: true, text: `${item.name} foi para a bolsa de ${unit?.name ?? 'seu personagem'}.` });
      await Promise.all([loadRoster(), loadInventory()]);
    } catch (e) {
      setEquipNotice({ ok: false, text: messageOf(e, 'Não consegui equipar este item.') });
    } finally {
      setEquipping(false);
    }
  };

  /*
   * Usa um reforço permanente na ficha de um personagem.
   *
   * Fora da partida, e no servidor: o motor não tem reforço permanente em
   * campo, e a gravação do esquadrão recusaria como trapaça um ponto de
   * atributo que os níveis não justificam. Aplicado aqui, ele passa a ser o
   * piso da ficha.
   */
  const useOnSheet = async (item: InventoryItem, characterId: number) => {
    const unit = rosterUnits.find((u) => u.characterId === characterId);
    setEquipping(true);
    setEquipNotice(null);
    try {
      const result = await api.post<{ stat: string; from: number; to: number }>(
        '/api/perfil/esquadrao/usar-item',
        { characterId, itemId: item.contentId },
      );
      const stat = STAT_NAMES[result.stat] ?? result.stat;
      setEquipNotice({
        ok: true,
        text: `${unit?.name ?? 'O personagem'} usou ${item.name}: ${stat} de ${result.from} para ${result.to}.`,
      });
      await Promise.all([loadRoster(), loadInventory()]);
    } catch (e) {
      setEquipNotice({ ok: false, text: messageOf(e, 'Não consegui usar este item.') });
    } finally {
      setEquipping(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaveStatus('saving');
    const ok = await updateProfile({
      avatarPortrait: selectedPortrait,
      bio: bioText,
      favoriteClass: selectedClass,
    });
    setSaveStatus(ok ? 'saved' : 'idle');
    setTimeout(() => setSaveStatus('idle'), 2500);
  };

  return (
    <section className="community-view" data-panel="comunidade">
      <div className="section-head">
        <h2>Comunidade, Perfil & Arsenal do Jogador</h2>
        <p className="muted">Estatísticas oficiais, esquadrão da campanha, itens do inventário e classificação geral.</p>
      </div>

      <div className="community-grid">
        {/* Painel do Jogador Atual */}
        <div className="community-card player-profile-card">
          <h3>Ficha do Comandante</h3>

          {user ? (
            <div className="profile-content">
              <div className="profile-top-row">
                <div
                  className="profile-avatar-large"
                  style={{
                    backgroundImage: `url(${ASSETS_BASE}/img/faces/${selectedPortrait}.webp)`,
                    backgroundSize: '400% 200%',
                    backgroundPosition: '0 0',
                  }}
                  title={user.username}
                />
                <div className="profile-identity">
                  <div className="identity-title">
                    <h4>{user.username}</h4>
                    <span className={`user-role-badge ${user.role}`}>{user.role.toUpperCase()}</span>
                  </div>
                  <p className="muted member-since">
                    Membro desde: {new Date(user.createdAt.replace(' ', 'T') + 'Z').toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>

              {/* Estatísticas Numéricas */}
              <div className="stats-badges-grid">
                <div className="stat-card">
                  <span className="stat-num">{stats?.skirmishesPlayed ?? 0}</span>
                  <span className="stat-label">Batalhas</span>
                </div>
                <div className="stat-card">
                  <span className="stat-num win">{stats?.skirmishesWon ?? 0}</span>
                  <span className="stat-label">Vitórias</span>
                </div>
                <div className="stat-card">
                  <span className="stat-num rate">{stats?.winRate ?? 0}%</span>
                  <span className="stat-label">Aproveitamento</span>
                </div>
                <div className="stat-card">
                  <span className="stat-num elo">⭐ {stats?.eloRating ?? 1000}</span>
                  <span className="stat-label">Pontos Elo</span>
                </div>
              </div>

              {/* Customização do Perfil */}
              <div className="profile-edit-section">
                <div className="form-row">
                  <label htmlFor="select-portrait">Retrato do Comandante:</label>
                  <select
                    id="select-portrait"
                    value={selectedPortrait}
                    onChange={(e) => setSelectedPortrait(e.target.value)}
                  >
                    {AVAILABLE_PORTRAITS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <label htmlFor="select-class">Classe Especialista:</label>
                  <select
                    id="select-class"
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                  >
                    {AVAILABLE_CLASSES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <label htmlFor="bio-input">Biografia Tática:</label>
                  <textarea
                    id="bio-input"
                    rows={3}
                    maxLength={280}
                    placeholder="Escreva sobre suas táticas e estratégia de combate…"
                    value={bioText}
                    onChange={(e) => setBioText(e.target.value)}
                  />
                </div>

                <div className="profile-save-row">
                  <button
                    type="button"
                    className="save-profile-btn"
                    onClick={() => void handleSaveProfile()}
                    disabled={saveStatus === 'saving'}
                  >
                    {saveStatus === 'saving'
                      ? 'Salvando…'
                      : saveStatus === 'saved'
                      ? '✓ Perfil Atualizado!'
                      : 'Salvar Perfil'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="guest-profile-prompt">
              <div className="prompt-icon">🛡️</div>
              <h4>Você está jogando como Convidado</h4>
              <p className="muted">
                Cadastre-se ou entre em sua conta para salvar o progresso dos seus personagens no Modo História,
                manter suas armas e itens e disputar posições no ranking oficial.
              </p>
              <button type="button" className="auth-open-btn large" onClick={onOpenAuth}>
                🔑 Criar Conta ou Entrar
              </button>
            </div>
          )}
        </div>

        {/* Tabela de Classificação Geral */}
        <div className="community-card leaderboard-card">
          <div className="card-head">
            <h3>Classificação Geral dos Comandantes</h3>
            <button
              type="button"
              className="refresh-btn"
              onClick={() => void loadLeaderboard()}
              title="Atualizar ranking"
            >
              🔄
            </button>
          </div>

          {loadingLb ? (
            <p className="muted">carregando classificação…</p>
          ) : leaderboard.length === 0 ? (
            <p className="muted">Nenhum comandante ranqueado no momento. Vença uma escaramuça para estrear a tabela!</p>
          ) : (
            <div className="table-wrapper">
              <table className="leaderboard-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Comandante</th>
                    <th>Especialidade</th>
                    <th>Vitórias</th>
                    <th>Taxa</th>
                    <th>Pontos</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((entry, idx) => (
                    <tr
                      key={entry.id}
                      className={user?.id === entry.id ? 'current-user-row' : ''}
                    >
                      <td className="rank-col">
                        {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                      </td>
                      <td className="user-col">
                        <div className="table-user-cell">
                          <span
                            className="table-avatar"
                            style={{
                              backgroundImage: `url(${ASSETS_BASE}/img/faces/${entry.avatarPortrait}.webp)`,
                              backgroundSize: '400% 200%',
                              backgroundPosition: '0 0',
                            }}
                          />
                          <span className="table-username">{entry.username}</span>
                          {entry.role === 'dono' && <span className="mini-badge dono">DONO</span>}
                          {entry.role === 'admin' && <span className="mini-badge admin">ADMIN</span>}
                        </div>
                      </td>
                      <td className="class-col">{entry.favoriteClass}</td>
                      <td className="wins-col win">{entry.skirmishesWon}</td>
                      <td className="rate-col">{entry.winRate}%</td>
                      <td className="elo-col">⭐ {entry.eloRating}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Seção de Personagens & Inventário do Usuário */}
      {user && (
        <div className="player-assets-grid">
          {/* Esquadrão / Personagens */}
          <div className="community-card roster-section-card">
            <div className="card-head">
              <div>
                <h3>🛡️ Meus Personagens (Esquadrão da Campanha)</h3>
                <p className="muted card-desc">
                  Personagens disponíveis para combate. Ganham experiência e sobem de nível nas batalhas do Modo História.
                </p>
              </div>
              <button
                type="button"
                className="refresh-btn"
                onClick={() => void loadRoster()}
                title="Atualizar esquadrão"
              >
                🔄
              </button>
            </div>

            {loadingRoster ? (
              <p className="muted">Carregando esquadrão…</p>
            ) : rosterUnits.length === 0 ? (
              <p className="muted">Nenhum personagem registrado no momento.</p>
            ) : (
              <div className="roster-units-grid">
                {rosterUnits.map((u) => (
                  <div key={u.characterId} className="roster-unit-card">
                    <div className="unit-card-header">
                      <UnitPortrait unit={u} />
                      <div className="unit-identity">
                        <div className="unit-title-line">
                          <h4 className="unit-name">{u.name}</h4>
                          <span className="unit-class-badge">{u.className}</span>
                        </div>
                        <div className="unit-level-info">
                          <span className="unit-level-badge">Nível {u.level}</span>
                          <span className="unit-exp-text">{u.exp} / 100 EXP</span>
                        </div>
                        <div className="exp-bar-track">
                          <div
                            className="exp-bar-fill"
                            style={{ width: `${Math.min(100, Math.max(0, u.exp))}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Atributos */}
                    <div className="unit-stats-grid">
                      <div className="unit-stat-cell" title="Pontos de Vida">
                        <span className="s-label">PV</span>
                        <span className="s-val hp">{u.stats.hp ?? 0}</span>
                      </div>
                      <div className="unit-stat-cell" title="Força">
                        <span className="s-label">FOR</span>
                        <span className="s-val">{u.stats.str ?? 0}</span>
                      </div>
                      <div className="unit-stat-cell" title="Magia">
                        <span className="s-label">MAG</span>
                        <span className="s-val">{u.stats.mag ?? 0}</span>
                      </div>
                      <div className="unit-stat-cell" title="Habilidade">
                        <span className="s-label">HAB</span>
                        <span className="s-val">{u.stats.skl ?? 0}</span>
                      </div>
                      <div className="unit-stat-cell" title="Velocidade">
                        <span className="s-label">VEL</span>
                        <span className="s-val">{u.stats.spd ?? 0}</span>
                      </div>
                      <div className="unit-stat-cell" title="Sorte">
                        <span className="s-label">SOR</span>
                        <span className="s-val">{u.stats.lck ?? 0}</span>
                      </div>
                      <div className="unit-stat-cell" title="Defesa">
                        <span className="s-label">DEF</span>
                        <span className="s-val">{u.stats.def ?? 0}</span>
                      </div>
                      <div className="unit-stat-cell" title="Resistência">
                        <span className="s-label">RES</span>
                        <span className="s-val">{u.stats.res ?? 0}</span>
                      </div>
                    </div>

                    {/* Equipamento / Bolsa da unidade */}
                    <div className="unit-bag-section">
                      <span className="bag-title">Equipamento & Bolsa:</span>
                      {u.inventory.length === 0 ? (
                        <span className="muted bag-empty">Sem itens equipados</span>
                      ) : (
                        <div className="bag-items-list">
                          {u.inventory.map((item, idx) => (
                            <div key={idx} className="bag-item-pill">
                              <ItemIcon icon={item.icon} iconSheet={item.iconSheet} type={item.type} size={18} />
                              <span className="bag-item-name">
                                {item.name || `#${item.itemId ?? item.weaponId}`}
                              </span>
                              <span className="bag-item-uses">{item.usesLeft} usos</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Inventário Geral */}
          <div className="community-card inventory-section-card">
            <div className="card-head">
              <div>
                <h3>📦 Meu Inventário & Arsenal (Itens da Conta)</h3>
                <p className="muted card-desc">
                  Itens e armas comprados na loja ou ganhos na campanha. Uma arma — ou uma poção,
                  um antídoto, uma chave — só entra em batalha depois de levada para a bolsa de um
                  personagem (até {BAG_LIMIT} espaços). Reforço de atributo se usa na ficha, e vale
                  para sempre.
                </p>
              </div>
              <button
                type="button"
                className="refresh-btn"
                onClick={() => void loadInventory()}
                title="Atualizar inventário"
              >
                🔄
              </button>
            </div>

            {equipNotice && (
              <p className={equipNotice.ok ? 'good inv-equip-notice' : 'bad inv-equip-notice'}>{equipNotice.text}</p>
            )}

            {loadingInventory ? (
              <p className="muted">Carregando inventário…</p>
            ) : inventoryItems.length === 0 ? (
              <p className="muted">Seu inventário está vazio no momento.</p>
            ) : (
              <div className="inventory-items-grid">
                {inventoryItems.map((item, idx) => (
                  <div key={`${item.contentType}-${item.contentId}-${idx}`} className="inventory-item-card">
                    <div className="inv-item-top">
                      <div className="inv-icon-box">
                        <ItemIcon icon={item.icon} type={item.type} size={30} />
                      </div>
                      <div className="inv-item-main">
                        <div className="inv-item-header">
                          <strong className="inv-name">{item.name}</strong>
                          <span className="inv-qty-badge">x{item.quantity}</span>
                        </div>
                        <span className={`inv-type-badge ${item.contentType}`}>
                          {item.type || (item.contentType === 'weapons' ? 'Arma' : 'Consumível')}
                        </span>
                      </div>
                    </div>
                    {item.description && (
                      <p className="inv-desc">{item.description}</p>
                    )}
                    {(() => {
                      // Arma sempre vai para a bolsa; item vai para onde o
                      // efeito dele manda (ver `use`, do servidor).
                      const destino = item.contentType === 'weapons' ? 'batalha' : (item.use ?? 'nenhum');

                      if (destino === 'batalha') {
                        return (
                          <select
                            className="inv-equip"
                            value=""
                            disabled={equipping || rosterUnits.length === 0}
                            onChange={(e) => {
                              const target = Number(e.target.value);
                              if (target > 0) void equip(item, target);
                            }}
                            aria-label={`Levar ${item.name} para a bolsa de um personagem`}
                          >
                            <option value="">🎒 Levar para a bolsa de…</option>
                            {rosterUnits.map((u) => (
                              <option
                                key={u.characterId}
                                value={u.characterId}
                                disabled={u.inventory.length >= BAG_LIMIT}
                              >
                                {u.name} ({u.inventory.length}/{BAG_LIMIT})
                              </option>
                            ))}
                          </select>
                        );
                      }

                      if (destino === 'ficha') {
                        const stat = item.boost ? (STAT_NAMES[item.boost.stat] ?? item.boost.stat) : 'atributo';
                        return (
                          <select
                            className="inv-equip inv-boost"
                            value=""
                            disabled={equipping || rosterUnits.length === 0}
                            onChange={(e) => {
                              const target = Number(e.target.value);
                              if (target > 0) void useOnSheet(item, target);
                            }}
                            aria-label={`Usar ${item.name} na ficha de um personagem`}
                          >
                            <option value="">
                              ⬆ Usar na ficha de… (+{item.boost?.amount ?? 1} de {stat}, para sempre)
                            </option>
                            {rosterUnits.map((u) => (
                              <option key={u.characterId} value={u.characterId}>
                                {u.name} — {stat} {u.stats[item.boost?.stat as keyof typeof u.stats] ?? 0}
                              </option>
                            ))}
                          </select>
                        );
                      }

                      return (
                        <p className="inv-note muted">
                          Item do RPG sem equivalente no tabuleiro — fica na conta, sem uso por enquanto.
                        </p>
                      );
                    })()}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}


